/**
 * Created by rathompson on 10/13/23
 * File: SubtenantAcquisitionModal.tsx
 * Project: cloud-mgmt-modules ©
 * ©
 */


import type { MouseEventHandler, VFC } from 'react'
import {
  Banner,
  Body, Bold,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  ModalProps,
  useIsMounted
} from '@panwds/react-ui'
import {
  BUTTON_ACCEPT,
  BUTTON_REJECT
} from '../../../common/constants/LabelConstants'
import {
  BUTTON_CLOSE,
  ERROR_ACQ_KEY_INVALID,
  FORM_FIELD_ACQUISITION_KEY,
  LABEL_ACQ_ALREADY_ACKNOWLEDGED,
  LABEL_ACQ_ALREADY_COMPLETE,
  LABEL_TSG_INITIATED_ACQ,
  LABEL_UNABLE_TO_FIND_ACQ_KEY,
  LINK_RESEND_ACQ_EMAIL,
  PLACEHOLDER_ACQ_KEY,
  TITLE_ACQ_FOR,
  TITLE_ACQ_FROM,
} from '../../constants/LabelConstants'
import {
  Form,
  SubmitButton,
  Input
} from '@panwds/react-form'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import AuthContext from '../../../common/context/AuthContext'
import _ from 'lodash'
import { caseInsensitiveIsEqual, safeSet } from '../../../common/utils/Utils'
import styled from 'styled-components'
import Link from '../../../common/components/link/Link'
import ScreenLoader from '../../../common/components/screenLoader/ScreenLoader'
import GlobalContext from '../../../common/context/GlobalContext'
import StringTemplate from '../../../common/utils/StringTemplate'
import { useFormContext } from 'react-hook-form'
import validator from 'validator'
import isUUID = validator.isUUID
import TenancyService from '../../services/TenancyService'
import Collapse from '../../../common/components/collapse/Collapse'
import { LoadAcqList, LoadTreeDataAsync } from '../../../common/actions/GlobalActions'
import moment from 'moment'
import { onExpandedKey, selectTreeNode } from '../../../common/reducers/actions/TreeReducer.actions'
import { switchTsg } from '../../../common/utils/Sparky'

const FORM_CONTAINER = styled.div`
  flex: 1 1;
  display: flex;
  flex-direction: column;

  form {
    flex: 1 1;
    display: flex;
    flex-direction: column;
  }
`

const Footer: VFC<{
  isApprover: boolean,
  onReject: MouseEventHandler<HTMLButtonElement>
}> = ({ isApprover, onReject }) => {

  const { formState } = useFormContext()
  const { isValid } = formState

  const disabled = !isApprover || !isValid

  return (
    <ModalFooter>
      <Button {...{ disabled }} onClick={onReject} appearance="secondary-destructive">{BUTTON_REJECT}</Button>
      <SubmitButton {...{ disabled }}>{BUTTON_ACCEPT}</SubmitButton>
    </ModalFooter>
  )
}

const SubtenantAcqForm: VFC<{
  showBanner: boolean,
  acquisition: any,
  error: string,
  tenantName: string,
  isApprover: boolean,
  onResendClick: any,
  onReject: any,
  acquisition_key?: string,
  title: string
}> = ({ showBanner, acquisition, error, tenantName, isApprover, onResendClick, onReject, acquisition_key, title }) => {

  const { setValue } = useFormContext()

  useEffect(() => {
    if (acquisition_key) {
      setValue('acquisition_key', acquisition_key, { shouldDirty: true })
    }
  }, [acquisition_key, setValue])

  return (
    <>
      <ModalHeader {...{ title }}/>
      <Collapse isOpen={showBanner}>
        <Banner appearance="success" showIcon>
          <Body>Email resent successfully</Body>
        </Banner>
      </Collapse>
      <ModalBody addClassName="cm-flex cm-flex-col">
        {error && <Bold appearance="semibold" addClassName="tw-text-error">{error}</Bold>}
        <div style={{ flexGrow: 1 }}>
          <Body size="md">{StringTemplate(LABEL_TSG_INITIATED_ACQ, [acquisition?.source_tsg?.display_name || acquisition?.source_tsg_id, tenantName])}</Body>
        </div>
        <div className={`cm-flex cm-flex-col cm-gap-2 ${!!acquisition_key && 'cm-justify-center'}`.trim()} style={{ flexGrow: 2 }}>
          <Body size='md' addClassName='cm-font-semibold cm-text-secondary'>{FORM_FIELD_ACQUISITION_KEY}</Body>
          <Input
            disabled={!isApprover || !!acquisition_key}
            name={'acquisition_key'}
            showLabel={false}
            label={FORM_FIELD_ACQUISITION_KEY}
            readOnly={!isApprover || !!acquisition_key}
            placeholder={PLACEHOLDER_ACQ_KEY}
            register={{
              required: true,
              validate: {
                isUUID: value => isUUID(value) || ERROR_ACQ_KEY_INVALID,
                isKeyValid: value => value === acquisition.id || ERROR_ACQ_KEY_INVALID
              }
            }}
          />
        </div>
        {isApprover && !acquisition_key && (
          <div className="cm-flex cm-gap-1 cm-text-center">
            <Body size='md'>{LABEL_UNABLE_TO_FIND_ACQ_KEY}</Body>
            <Link onClick={onResendClick} addClassName="cm-text-sm" size="sm">{LINK_RESEND_ACQ_EMAIL}</Link>
          </div>
        )}
      </ModalBody>
      <Footer {...{ isApprover, onReject }} />
    </>
  )
}

const CompletedAcqBody: VFC<{ acquisition: any, tenantName: string, title: string }> = ({ acquisition, tenantName, title }) => {

  const userName = useMemo(() => {
    if (acquisition?.acknowledged_by?.first_name && acquisition?.acknowledged_by?.last_name) {
      return `${acquisition.acknowledged_by.first_name} ${acquisition.acknowledged_by.last_name}`
    }
    return acquisition?.acknowledged_by?.email
  }, [acquisition])

  const body = useMemo(() => {
    const response_timestamp = moment(acquisition.response_timestamp).format('MM/DD/YYYY h:mm:ss a')
    if (userName) {
      return StringTemplate(LABEL_ACQ_ALREADY_COMPLETE, [tenantName, acquisition.status, userName, response_timestamp])
    }
    else {
      return StringTemplate(LABEL_ACQ_ALREADY_ACKNOWLEDGED, [tenantName, acquisition.status, response_timestamp])
    }
  }, [acquisition.response_timestamp, acquisition.status, tenantName, userName])

  return (
    <div className='cm-flex cm-flex-col cm-h-full cm-flex-1'>
      <ModalHeader {...{ title }}/>
      <ModalBody>
        <Body>{body}</Body>
      </ModalBody>
      <ModalFooter>
        <ModalCloseButton appearance='secondary'>{BUTTON_CLOSE}</ModalCloseButton>
      </ModalFooter>
    </div>
  )
}

const SubtenantAcquisitionModal: VFC<ModalProps & {
  acquisition: any,
  closeModal: any,
  acquisition_key?: string
}> = ({ isOpen, acquisition, closeModal: onClose, acquisition_key }) => {

  const { treeReducer, dispatch } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer
  const { user } = useContext(AuthContext)

  const isMounted = useIsMounted()
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [showBanner, setShowBanner] = useState<boolean>(false)

  const tenantName = acquisition?.target_tsg?.display_name || acquisition?.target_tsg_id
  const isPending = acquisition.status === 'pending'

  const onSubmit = useCallback(async ({ acquisition_key }) => {
    try {
      safeSet(setLoading, true, isMounted.current)
      safeSet(setError, '', isMounted.current)
      await TenancyService.acquisitionRequests.acknowledge(selectedTreeNode?.id, acquisition.id, { approved: true, acquisition_key })
        .then(() => onClose())
        .then(() => LoadAcqList(selectedTreeNode?.id, dispatch))
        .then(() => LoadTreeDataAsync(dispatch))
        .then(() => {
          switchTsg(selectedTreeNode?.id, { forceReload: true })
          dispatch(selectTreeNode({ ...selectedTreeNode }))
        })
        .then(() => dispatch(onExpandedKey([selectedTreeNode?.id])))
    }
    catch (error: any) {
      safeSet(setError, error.message, isMounted.current)
    }
    finally {
      safeSet(setLoading, false, isMounted.current)
    }
  }, [acquisition.id, dispatch, isMounted, onClose, selectedTreeNode])

  const onReject = useCallback(async () => {
    try {
      safeSet(setLoading, true, isMounted.current)
      safeSet(setError, '', isMounted.current)
      await TenancyService.acquisitionRequests.acknowledge(selectedTreeNode?.id, acquisition.id, { approved: false, acquisition_key: acquisition.id })
        .then(() => onClose())
        .finally(() => LoadAcqList(selectedTreeNode?.id, dispatch))
    }
    catch (error: any) {
      safeSet(setError, error.message, isMounted.current)
    }
    finally {
      safeSet(setLoading, false, isMounted.current)
    }
  }, [acquisition.id, dispatch, isMounted, onClose, selectedTreeNode?.id])

  const isApprover = useMemo(() => {
    const email = user?.email
    const approvers = _.get(acquisition, 'approvers', [])
    return email && approvers.some((approver: any) => caseInsensitiveIsEqual(approver.email, email))
  }, [acquisition, user?.email])

  const triggerBanner = useCallback(() => {
    safeSet(setShowBanner, true, isMounted.current)
    setTimeout(() => {
      safeSet(setShowBanner, false, isMounted.current)
    }, 2500)
  }, [isMounted])

  const onResendClick = useCallback(async () => {
    try {
      safeSet(setLoading, true, isMounted.current)
      safeSet(setError, '', isMounted.current)
      safeSet(setShowBanner, false, isMounted.current)
      await TenancyService.acquisitionRequests.resendEmail(acquisition.target_tsg_id, acquisition.id)
      triggerBanner()
    }
    catch (error: any) {
      safeSet(setError, error.message, isMounted.current)
    }
    finally {
      safeSet(setLoading, false, isMounted.current)
    }
  }, [acquisition.id, triggerBanner, acquisition.target_tsg_id, isMounted])

  const title: string = useMemo(() => {
    if (acquisition.type === 'outbound') {
      // Acquisition for
      const value = acquisition?.target_tsg?.display_name || acquisition?.target_tsg_id
      return StringTemplate(TITLE_ACQ_FOR, [value]) as any
    }
    else {
      // Acquisition request from
      const value = acquisition?.source_tsg?.display_name || acquisition?.source_tsg_id
      return StringTemplate(TITLE_ACQ_FROM, [value]) as any
    }
  }, [acquisition])

  return (
    <Modal {...{ isOpen, onClose }}>
      <FORM_CONTAINER>
        {loading && <ScreenLoader appearance="light"/>}
        {isPending ? (
          <Form {...{ onSubmit }} mode="onChange">
            <SubtenantAcqForm {...{ onReject, onResendClick, acquisition, isApprover, tenantName, error, showBanner, acquisition_key, title }} />
          </Form>
        ) : (
          <CompletedAcqBody {...{ acquisition, tenantName, title }}/>
        )}
      </FORM_CONTAINER>
    </Modal>
  )
}

export default SubtenantAcquisitionModal
